import { CustomItemDataType, DonationFundPlatformTypeEnum } from '@graphql/generated-base';
import { useTranslation } from '@shared/core';
import { useCallback } from 'react';
import { PLATFORM_LABELS } from '../../CheckoutDialog.constants';
import { useCheckoutFunnel } from '../../hooks/useCheckoutFunnel';
import { useCheckoutInitDataContext } from '../../hooks/useCheckoutInitData';
import { useCheckoutTelemetryData, useGetProductData } from '@apps/registry/guest/components/CheckoutDialog/hooks';
import { withWindow } from '@shared/utils/withWindow';
import { runIfFn } from '@shared/utils/functions';
import { addAction } from '@shared/utils/logger';
import { useShoppingCartTelemetry } from '@apps/registry/common/components/ShoppingCart/ShoppingCart.telemetry';

const VENMO_PAYMENT_URL = 'https://account.venmo.com/payment-link';

function buildVenmoPaymentLink(username: string, amount: string, note?: string) {
  const url = new URL(VENMO_PAYMENT_URL);

  const params = new URLSearchParams({
    txn: 'pay',
    recipients: username,
    amount,
  });

  if (note) {
    params.append('note', note);
  }

  url.search = params.toString();

  return url.toString();
}

export const useExternalCheckoutReviewController = () => {
  const {
    formValues,
    initiateCancelProcess,
    toNext,
    context: { isAffiliate, isGroupGifting },
  } = useCheckoutFunnel(({ formValues, initiateCancelProcess, toNext, context }) => [formValues, initiateCancelProcess, toNext, context.isAffiliate, context.isGroupGifting]);
  const { registryItem } = useCheckoutInitDataContext();
  const { buyRegistryItem } = useShoppingCartTelemetry();
  const { t } = useTranslation('guestRegistry');
  const { product, productImageSrc, siteName, currency } = useGetProductData('externalCheckoutReview');

  const translations = t('checkoutDialog', 'screens', 'externalCheckoutReview');
  const isCharity = registryItem?.donationFund?.fundType === CustomItemDataType.charity;
  const charityTitle = registryItem?.productData.title;
  const platformLabel = isAffiliate ? siteName : isCharity ? charityTitle : formValues?.paymentMethod ? PLATFORM_LABELS[formValues?.paymentMethod] : '';
  const isCashFundType = registryItem?.donationFund?.fundType === 'cash';
  const { registryItemId, reservedQty, priceValueInMinorUnits, typeOfItem, priceCurrencyCode, productTitle } = useCheckoutTelemetryData();

  const getRedirectUrl = useCallback(
    (method?: 'contribute' | 'purchase'): string => {
      if (isGroupGifting && method === 'contribute') {
        if (registryItem?.donationFund?.platform?.type === DonationFundPlatformTypeEnum.venmo) {
          return buildVenmoPaymentLink(registryItem?.donationFund?.platform?.username || '', formValues.amount?.toString() || '', formValues.note);
        }

        return registryItem?.donationFund?.platform?.link || '';
      }

      return registryItem?.productData?.checkoutUrl || '';
    },
    [registryItem, formValues.amount, formValues.note, isGroupGifting],
  );

  const handleRedirectToRetailer = useCallback(
    (method?: 'contribute' | 'purchase') => {
      const url = getRedirectUrl(method);
      withWindow(window => {
        window.open(url, '_blank');
      });
      runIfFn(toNext);
    },
    [toNext, getRedirectUrl],
  );

  const handleGoToButton = useCallback(
    (method?: 'contribute' | 'purchase') => {
      const buyRegistryItemData = {
        productTitle,
        registryItemId,
        reservedQty,
        priceValueInMinorUnits,
        priceCurrencyCode,
        itemType: typeOfItem,
        buttonLabel: translations.goToButton({ platform: platformLabel }),
        redirectUrl: registryItem?.productData.externalUrl,
        action: 'redirect',
        method: method || 'purchase',
        isGroupGiftingEnabled: isGroupGifting,
      };
      buyRegistryItem(buyRegistryItemData);
      addAction('buyRegistryItem', buyRegistryItemData);
      handleRedirectToRetailer(method);
    },
    [
      buyRegistryItem,
      isGroupGifting,
      platformLabel,
      priceCurrencyCode,
      priceValueInMinorUnits,
      productTitle,
      handleRedirectToRetailer,
      registryItem?.productData.externalUrl,
      registryItemId,
      reservedQty,
      translations,
      typeOfItem,
    ],
  );

  return {
    formValues,
    initiateCancelProcess,
    redirectToExternalGateway: handleGoToButton,
    translations,
    platformLabel,
    isCharity,
    isAffiliate,
    product,
    currency,
    productImageSrc,
    isCashFundType,
  };
};
